// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import getRegionalSalesRep from "@stoneage/App/Handlers/RegionalSalesRep/getRegionalSalesRep";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isLoading: state.xRegionalSalesRep.isLoading,
    salesRep: state.xRegionalSalesRep.data?.regionalSalespersons
});

const mapDispatchToProps = {
    getRegionalSalesRep
};

interface RegionalSalesRepStyles {
    centeringWrapper?: InjectableCss;
    container?: InjectableCss;
    title?: TypographyProps;
    salesRepCard?: InjectableCss;
    salesRegion?: TypographyProps;
    salesName?: TypographyProps;
    salesPhone?: TypographyProps;
    salesEmail?: TypographyProps;
};

const styles: RegionalSalesRepStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: center;
            height: 200px;
            width: 100%;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-right: -22px;
            ${xScreen("lte", "xs")} {
                margin-right: 0;
            }
        `,
    },
    title: {
        size: "24px",
        lineHeight: "26px",
        variant: "h2",
        weight: "bold",
        color: "common.black",
        css: css`
            margin-bottom: 16px;
            width: 100%;
        `,
    },
    salesRepCard: {
        css: css`
            width: calc(100%/4 -  22px);
            margin-right: 22px;
            margin-bottom: 40px;
            padding-top: 8px;
            border-top: 1px solid ${getColor("common.ui6")};
            ${xScreen("lte", "md")} {
                width: calc(100%/3 -  22px);
            }
            ${xScreen("lte", "sm")} {
                width: calc(100%/2 -  22px);
            }
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
            }
        `,
    },
    salesRegion: {
        size: "12px",
        lineHeight: "18px",
        weight: "normal",
        css: css`
            display: block;
            margin-bottom: 4px;
        `,
    },
    salesName: {
        size: "15px",
        lineHeight: "26px",
        css: css`
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
        `,
    },
    salesPhone: {
        size: "14px",
        lineHeight: "22px",
        color: "common.black",
        weight: "bold",
        css: css`
            display: block;
            margin-bottom: 4px;
        `,
    },
    salesEmail: {
        size: "14px",
        lineHeight: "22px",
        color: "common.black",
        weight: "bold",
        css: css`
            display: block;
        `,
    },
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const RegionalSalesRep = ({
    salesRep,
    getRegionalSalesRep,
    isLoading,
    fields: { title }
}: Props) => {
    useEffect(() => {
        if(!salesRep?.length && !isLoading) {
            getRegionalSalesRep({});
        }
    }, []);

    if(isLoading && !salesRep?.length) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner size={25} />
            </StyledWrapper>
        );
    }

    if(!isLoading && !salesRep?.length) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <Typography as="h2" {...styles.title}>{title}</Typography>
            {salesRep?.map(sales => (
                <StyledWrapper translate="no" {...styles.salesRepCard} key={`${uniqueId()}`}>
                    <Typography {...styles.salesRegion}>{sales.region}</Typography>
                    <Typography {...styles.salesName}>{sales.name}</Typography>
                    <Typography {...styles.salesPhone}>{sales.phone}</Typography>
                    <Typography {...styles.salesEmail}>{sales.email}</Typography>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RegionalSalesRep),
    definition: {
        displayName: "Regional Sales Rep",
        group: "XNGAGE" as any,
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable"
            }
        ]
    }
};

export default widgetModule;
